import { cn, getTranslatedMessage } from '@/utils';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox as CheckboxAntd } from 'antd';
import { CheckboxChangeEvent, CheckboxGroupProps } from 'antd/es/checkbox';

import './style.scss';
import { Namespace } from 'i18next';

enum ECheckboxGroupValue {
	YES = 'YES',
	NO = 'NO',
}

export interface ICheckboxGroup extends CheckboxGroupProps {
	label?: string;
	containerClassName?: string;
	require?: boolean;
	horizontal?: boolean;
	labelClassName?: string;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	ns?: Namespace;
	checkAll?: boolean;
	showYesNo?: boolean;
	yesLabel?: string;
	noLabel?: string;
}
const CheckboxGroup: React.FC<ICheckboxGroup> = ({
	label,
	containerClassName = '',
	require = false,
	horizontal = true,
	labelClassName,
	touched,
	errorCode,
	hasServerError = false,
	className,
	ns,
	checkAll,
	value = [],
	onChange,
	showYesNo = false,
	yesLabel = 'yes',
	noLabel = 'no',
	...rest
}) => {
	const { t } = useTranslation(ns);

	const _options = useMemo(() => {
		const options = [...(rest?.options || [])] as CheckboxGroupProps['options'];
		if (options?.length) {
			return options.map((option) => {
				if (typeof option === 'object') {
					return {
						...option,
						label: getTranslatedMessage(t, option.label, { ns }),
					};
				}
				return option;
			});
		}
		return options;
	}, [rest?.options, t, ns]);

	const checkboxOptions = useMemo(() => {
		const options = [];

		// Only show YES if there are base options
		if (showYesNo && _options && _options.length > 0) {
			options.push({ label: t(yesLabel), value: ECheckboxGroupValue.YES });
		}

		if (_options && _options.length > 0) {
			options.push(..._options);
		}

		if (showYesNo) {
			options.push({ label: t(noLabel), value: ECheckboxGroupValue.NO });
		}

		return options;
	}, [showYesNo, yesLabel, noLabel, _options, t]);

	const allOptionValues = useMemo(() => {
		const baseOptions = (_options || []).map((option) =>
			typeof option === 'object' ? option.value : option
		);

		if (showYesNo) {
			// Only include YES if there are base options
			if (baseOptions.length > 0) {
				return [
					ECheckboxGroupValue.YES,
					...baseOptions,
					ECheckboxGroupValue.NO,
				];
			} else {
				return [ECheckboxGroupValue.NO];
			}
		}

		return baseOptions;
	}, [_options, showYesNo]);

	const isAllChecked =
		Array.isArray(value) &&
		allOptionValues.length > 0 &&
		allOptionValues.every((v) => value.includes(v));

	const handleCheckAllChange = useCallback(
		(e: CheckboxChangeEvent) => {
			if (onChange) {
				if (e.target.checked) {
					onChange(allOptionValues);
				} else {
					onChange([]);
				}
			}
		},
		[onChange, allOptionValues]
	);

	const handleGroupChange = useCallback(
		(list: any[]) => {
			// Logic for handling yes/no checkbox behavior
			if (showYesNo) {
				const hasYes = list.includes(ECheckboxGroupValue.YES);
				const hasNo = list.includes(ECheckboxGroupValue.NO);
				const previousValue = value || [];
				const wasYesChecked = previousValue.includes(ECheckboxGroupValue.YES);
				const wasNoChecked = previousValue.includes(ECheckboxGroupValue.NO);

				// If check yes, then check all options except no, but keep NO if it was already checked
				if (hasYes && !wasYesChecked) {
					const allOptionsExceptNo = allOptionValues.filter(
						(v) => v !== ECheckboxGroupValue.NO
					);
					// Keep NO if it was already checked
					const finalList = wasNoChecked
						? [...allOptionsExceptNo, ECheckboxGroupValue.NO]
						: allOptionsExceptNo;
					onChange?.(finalList);
					return;
				}

				// If uncheck yes, then uncheck all baseOptions
				if (!hasYes && wasYesChecked) {
					const baseOptions = allOptionValues.filter(
						(v) => v !== ECheckboxGroupValue.YES && v !== ECheckboxGroupValue.NO
					);
					const newList = list.filter((v) => !baseOptions.includes(v));
					onChange?.(newList);
					return;
				}

				// If check no, then just add no to current list
				// If uncheck no, then just remove no from current list
				if ((hasNo && !wasNoChecked) || (!hasNo && wasNoChecked)) {
					onChange?.(list);
					return;
				}

				// If uncheck any other option (not yes/no), then uncheck yes
				const baseOptions = allOptionValues.filter(
					(v) => v !== ECheckboxGroupValue.YES && v !== ECheckboxGroupValue.NO
				);
				const uncheckedOptions = baseOptions.filter(
					(opt) => !list.includes(opt)
				);

				if (uncheckedOptions.length > 0 && wasYesChecked) {
					// Some option was unchecked, so uncheck yes
					const newList = list.filter((v) => v !== ECheckboxGroupValue.YES);
					onChange?.(newList);
					return;
				}

				// If check all baseOptions, then automatically check yes
				const checkedBaseOptions = baseOptions.filter((opt) =>
					list.includes(opt)
				);
				if (
					checkedBaseOptions.length === baseOptions.length &&
					baseOptions.length > 0 &&
					!wasYesChecked
				) {
					// All baseOptions are checked, automatically check yes
					onChange?.([...list, ECheckboxGroupValue.YES]);
					return;
				}
			}

			onChange?.(list);
		},
		[onChange, showYesNo, allOptionValues, value]
	);

	return (
		<div
			className={cn(
				'flex gap-1',
				horizontal ? 'flex-row items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2 cursor-pointer',
						horizontal && 'min-w-[110px]',
						labelClassName
					)}
				>
					{label} {require && <span className="text-error">*</span>}
				</label>
			)}

			<div className="flex gap-2">
				{checkAll && (
					<CheckboxAntd
						checked={isAllChecked}
						onChange={handleCheckAllChange}
						disabled={rest?.disabled}
					>
						{t('all')}
					</CheckboxAntd>
				)}

				<CheckboxAntd.Group
					{...rest}
					options={checkboxOptions}
					value={value}
					onChange={handleGroupChange}
					className={cn(className, hasServerError && 'ant-checkbox-error')}
				/>
			</div>
		</div>
	);
};

export default memo(CheckboxGroup);
